
import React from 'react';
import { Facebook, Linkedin, Mail, Link2Icon, Check, PinIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { createShareableUrl } from '@/utils/url-utils';

// Custom X logo component (formerly Twitter)
const XLogo = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M6 2h12a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4Z" opacity="0" />
    <path d="M4 4l11.5 11.5" />
    <path d="M20 4L8.5 15.5" />
    <path d="M4 20l7.5-7.5" />
    <path d="M12.5 15.5l7.5 4.5" />
  </svg>
);

interface SocialShareButtonsProps {
  title: string;
  url: string;
  description?: string;
  image?: string;
}

export function SocialShareButtons({ title, url, description = '', image = '' }: SocialShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();
  
  // Ensure we have properly formatted absolute URLs
  const absoluteUrl = createShareableUrl(url);
  const absoluteImageUrl = image ? createShareableUrl(image) : '';
  
  // Log the URL for debugging
  console.log('Sharing URL:', absoluteUrl);
  
  // Encode parameters for sharing URLs
  const encodedUrl = encodeURIComponent(absoluteUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const encodedImage = encodeURIComponent(absoluteImageUrl);
  
  // Create sharing URLs for different platforms
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedImage}&description=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(absoluteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openShareLink = (platform: keyof typeof shareLinks) => {
    // If it's Pinterest and there's no image, show a message
    if (platform === 'pinterest' && !image) {
      console.warn('Pinterest sharing requires an image');
      return;
    }
    
    console.log(`Opening share link for ${platform}:`, shareLinks[platform]);
    window.open(shareLinks[platform], '_blank', 'noopener,noreferrer');
  };

  const buttonClassName = isMobile ? "h-8 w-8" : "h-9";
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-sm font-medium text-muted-foreground mr-1">Share:</p>
      
      <Button 
        variant="outline" 
        size="icon" 
        className={cn(buttonClassName, "rounded-full")}
        onClick={() => openShareLink('facebook')}
        aria-label="Share on Facebook"
      >
        <Facebook className="h-4 w-4" />
      </Button>
      
      <Button 
        variant="outline" 
        size="icon" 
        className={cn(buttonClassName, "rounded-full")}
        onClick={() => openShareLink('twitter')}
        aria-label="Share on X (formerly Twitter)"
      >
        <XLogo className="h-4 w-4" />
      </Button>
      
      <Button 
        variant="outline" 
        size="icon" 
        className={cn(buttonClassName, "rounded-full")}
        onClick={() => openShareLink('linkedin')}
        aria-label="Share on LinkedIn"
      >
        <Linkedin className="h-4 w-4" />
      </Button>

      <Button 
        variant="outline" 
        size="icon" 
        className={cn(buttonClassName, "rounded-full")}
        onClick={() => openShareLink('pinterest')}
        aria-label="Share on Pinterest"
        disabled={!image}
        title={!image ? "Pinterest sharing requires an image" : undefined}
      >
        <PinIcon className="h-4 w-4" />
      </Button>
      
      <Button 
        variant="outline" 
        size="icon" 
        className={cn(buttonClassName, "rounded-full")}
        onClick={() => openShareLink('email')}
        aria-label="Share via Email"
      >
        <Mail className="h-4 w-4" />
      </Button>
      
      <Button 
        variant="outline" 
        size="icon" 
        className={cn(buttonClassName, "rounded-full")}
        onClick={handleCopyLink}
        aria-label="Copy link"
      >
        {copied ? <Check className="h-4 w-4" /> : <Link2Icon className="h-4 w-4" />}
      </Button>
    </div>
  );
}
